/**
 * Brand and mode resolution utilities.
 *
 * Brand and mode are encoded together as "{brand}-{mode}" in a single "brand"
 * query param or cookie. Examples: "foodora-light", "talabat-dark".
 *
 * Priority: ?brand= query param > brand cookie > fallback ("foodpanda-light").
 *
 * Only b2b themes are supported.
 */
import { parse as parseCookies } from "cookie-es";

export const BRANDS = [
  "efood",
  "foodora",
  "foodpanda",
  "foody",
  "glovo",
  "hungerstation",
  "pedidosya",
  "talabat",
  "yemeksepeti",
] as const;

export const MODES = ["light", "dark"] as const;

export type Brand = (typeof BRANDS)[number];
export type Mode = (typeof MODES)[number];

/**
 * Human-readable display names for each brand, used in user-facing copy
 * (e.g. "Foodpanda Developer" on the login heading). Keep these aligned with
 * each brand's official capitalization.
 */
export const BRAND_DISPLAY_NAMES: Record<Brand, string> = {
  efood: "Efood",
  foodora: "Foodora",
  foodpanda: "Foodpanda",
  foody: "Foody",
  glovo: "Glovo",
  hungerstation: "HungerStation",
  pedidosya: "PedidosYa",
  talabat: "Talabat",
  yemeksepeti: "Yemeksepeti",
};

export interface ResolvedTheme {
  brand: Brand;
  mode: Mode;
}

const BRAND_SET = new Set<string>(BRANDS);
const MODE_SET = new Set<string>(MODES);

const FALLBACK: ResolvedTheme = { brand: "foodpanda", mode: "light" };

/**
 * Parses a "{brand}-{mode}" string into a ResolvedTheme.
 * Returns undefined if the value is missing, malformed, or contains unknown tokens.
 */
function parseThemeValue(value: string | null | undefined): ResolvedTheme | undefined {
  if (value == null) return undefined;

  // Split on the last hyphen so brand names with hyphens are handled correctly.
  const lastHyphen = value.lastIndexOf("-");
  if (lastHyphen === -1) return undefined;

  const brand = value.slice(0, lastHyphen);
  const mode = value.slice(lastHyphen + 1);

  if (BRAND_SET.has(brand) && MODE_SET.has(mode)) {
    return { brand: brand as Brand, mode: mode as Mode };
  }

  return undefined;
}

/**
 * Resolves the active brand and colour-scheme mode from the incoming request.
 * Reads a single "brand" query param or cookie whose value is "{brand}-{mode}".
 * Falls back to { brand: "foodpanda", mode: "light" } for unknown or missing values.
 */
export function resolveTheme(request: Request): ResolvedTheme {
  const url = new URL(request.url);
  const queryParam = url.searchParams.get("brand");
  const fromQuery = parseThemeValue(queryParam);

  if (fromQuery != null) {
    return fromQuery;
  }

  const cookieHeader = request.headers.get("cookie") ?? "";
  const cookies = parseCookies(cookieHeader);
  const fromCookie = parseThemeValue(cookies["brand"]);

  if (fromCookie != null) {
    return fromCookie;
  }

  return FALLBACK;
}

/**
 * Returns the data-theme attribute value for the given brand and mode.
 * Pattern: "{brand}-{mode}-b2b"
 */
export function brandDataTheme(brand: Brand, mode: Mode): string {
  return `${brand}-${mode}-b2b`;
}
